<template>
  <div class="tencent-map-modal">
    <el-dialog v-if="!embedded"
               title="地图定位"
               :visible.sync="visible"
               width="1200px"
               :close-on-click-modal="false"
               @close="handleClose">
      <div ref="mapContainer"
           class="map-container"
           :style="mapContainerStyle"></div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="handleClose">关闭</el-button>
      </span>
    </el-dialog>
    <div v-else
         ref="mapContainer"
         class="map-container"
         :style="mapContainerStyle"></div>
  </div>
</template>

<script>
export default {
  name: 'MapModal',
  props: {
    location: {
      type: String,
      default: ''
    },
    mapLocation: {
      type: String,
      default: ''
    },
    mapKey: {
      type: String,
      required: true
    },
    circleRadius: {
      type: Number,
      required: true
    },
    embedded: Boolean,
    height: {
      type: Number,
      default: 600
    }
  },
  data () {
    return {
      visible: false,
      map: null,
      marker: null,
      infoWindow: null,
      circle: null
    }
  },
  computed: {
    mapContainerStyle () {
      return {
        width: '100%',
        height: `${this.height}px`,
        border: '1px solid #ddd'
      }
    }
  },
  beforeDestroy () {
    this.handleClose()
  },
  methods: {
    show () {
      if (this.embedded) {
        if (this.map) {
          return
        }
        this.$nextTick(() => {
          setTimeout(() => {
            this.initMap()
          }, 100)
        })
        return
      }
      if (this.visible) {
        // 如果已经显示，不重复显示
        return
      }

      this.visible = true
      this.$nextTick(() => {
        // 延迟初始化地图，确保DOM已渲染
        setTimeout(() => {
          this.initMap()
        }, 100)
      })
    },
    handleClose () {
      this.visible = false

      // 清理地图资源
      if (this.infoWindow) {
        try {
          this.infoWindow.close()
          this.infoWindow.destroy()
        } catch (error) {
          console.warn('清理信息窗口失败:', error)
        }
        this.infoWindow = null
      }

      if (this.marker) {
        try {
          this.marker.destroy()
        } catch (error) {
          console.warn('清理标记失败:', error)
        }
        this.marker = null
      }

      if (this.circle) {
        try {
          this.circle.destroy()
        } catch (error) {
          console.warn('清理圆圈失败:', error)
        }
        this.circle = null
      }

      if (this.map) {
        try {
          this.map.destroy()
        } catch (error) {
          console.warn('销毁地图失败:', error)
        }
        this.map = null
      }
    },
    initMap () {
      if (!window.TMap) {
        this.loadMapScript()
        return
      }
      this.createMap()
    },
    loadMapScript () {
      // 检查TMap是否已经加载
      if (window.TMap) {
        console.log('腾讯地图API已存在，直接创建地图')
        this.createMap()
        return
      }

      // 检查是否已经有其他组件在加载API
      if (window.tencentMapLoading) {
        console.log('腾讯地图API正在加载中，等待加载完成')
        window.tencentMapLoading.then(() => {
          this.createMap()
        }).catch(() => {
          this.$message.error('地图加载失败，请检查网络连接')
        })
        return
      }

      console.log('开始加载腾讯地图API')

      // 创建加载Promise并存储到全局，避免重复加载
      window.tencentMapLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${this.mapKey}&libraries=service`
        script.onerror = () => {
          // 清除加载标识
          window.tencentMapLoading = null
          this.$message.error('地图加载失败，请检查网络连接')
          reject(new Error('地图加载失败'))
        }
        script.onload = () => {
          console.log('腾讯地图API加载成功')
          // 等待一下让service模块完全初始化
          setTimeout(() => {
            // 清除加载标识
            window.tencentMapLoading = null
            resolve()
          }, 500)
        }
        document.head.appendChild(script)
      })

      window.tencentMapLoading.then(() => {
        this.createMap()
      }).catch(() => {
        this.$message.error('地图加载失败，请检查网络连接')
      })
    },
    createMap () {
      if (!this.$refs.mapContainer) {
        return
      }
      // 默认中心点坐标（北京天安门）
      const center = new window.TMap.LatLng(39.908823, 116.397470)
      // 创建地图
      this.map = new window.TMap.Map(this.$refs.mapContainer, {
        center: center,
        zoom: 15,
        pitch: 0,
        rotation: 0
      })
      // 优先使用mapLocation（坐标），其次使用location（地址）
      if (this.mapLocation && this.mapLocation.trim()) {
        this.parseMapLocation(this.mapLocation.trim())
      } else if (this.location && this.location.trim()) {
        this.geocodeLocation(this.location.trim())
      } else {
        // 添加默认标记
        this.addMarker(center, '默认位置')
      }
    },
    geocodeLocation (address) {
      // 使用后端接口进行地址解析
      this.$ajax({
        url: '/support/thirdparty/tencent/map/geocoder',
        method: 'post',
        data: this.$qs.stringify({
          address: address,
          policy: 0
        })
      }).then(response => {
        if (response.data && response.data.responseCode === 'SUCCESS' && response.data.geocoderResponse) {
          const geocoderResponse = response.data.geocoderResponse
          if (geocoderResponse.status === 0 && geocoderResponse.result && geocoderResponse.result.location) {
            const location = geocoderResponse.result.location
            const latLng = new window.TMap.LatLng(location.lat, location.lng)
            // 设置地图中心
            this.map.setCenter(latLng)
            this.map.setZoom(17)
            // 添加标记，使用解析结果的标题或原地址
            const title = geocoderResponse.result.title || address
            this.addMarker(latLng, title)
          } else {
            this.showDefaultLocation(address)
          }
        } else {
          this.showDefaultLocation(address)
        }
      }).catch(() => {
        this.showDefaultLocation(address)
      })
    },
    // 显示默认位置
    showDefaultLocation (address) {
      this.$message.warning(`地址"${address}"解析失败，显示默认位置`)
      const center = new window.TMap.LatLng(39.908823, 116.397470)
      this.addMarker(center, '默认位置')
    },
    addMarker (position, title) {
      if (this.marker) {
        this.marker.destroy()
      }
      // 创建标记
      this.marker = new window.TMap.MultiMarker({
        map: this.map,
        styles: {
          'marker': new window.TMap.MarkerStyle({
            'width': 25,
            'height': 35,
            'anchor': { x: 16, y: 32 },
            'src': 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/markerDefault.png'
          })
        },
        geometries: [{
          id: 'marker1',
          styleId: 'marker',
          position: position,
          properties: {
            title: title
          }
        }]
      })

      // 添加500米圆圈
      this.addCircle(position)
    },
    // 解析地图坐标（格式：纬度,经度 或 纬度 经度）
    parseMapLocation (mapLocation) {
      try {
        // 支持多种分隔符：逗号、空格、分号
        const coords = mapLocation.replace(/[,;]/g, ' ').split(/\s+/).filter(item => item.trim())
        if (coords.length >= 2) {
          const lat = parseFloat(coords[0])
          const lng = parseFloat(coords[1])
          // 验证坐标范围
          if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            const latLng = new window.TMap.LatLng(lat, lng)
            // 设置地图中心
            this.map.setCenter(latLng)
            this.map.setZoom(17)

            // 添加标记
            this.addMarker(latLng, '坐标定位')

            // 进行反向地理编码获取地址，并在气泡中显示
            this.reverseGeocode(latLng, `${lat},${lng}`)
            return
          }
        }
        // 坐标解析失败，显示默认位置
        this.showDefaultLocation(`无效坐标: ${mapLocation}`)
      } catch (error) {
        // 解析异常，显示默认位置
        this.showDefaultLocation(`坐标解析错误: ${mapLocation}`)
      }
    },
    // 反向地理编码获取地址
    reverseGeocode (latLng, coords) {
      console.log('开始反向地理编码:', latLng.lat, latLng.lng)

      this.$ajax({
        url: '/support/thirdparty/tencent/map/reverseGeocoder',
        method: 'post',
        data: this.$qs.stringify({
          lat: latLng.lat,
          lng: latLng.lng,
          coordType: 5
        })
      }).then(response => {
        console.log('反向地理编码响应:', response.data)
        if (response.data && response.data.errorCode === 0) {
          const reverseGeocoderResponse = response.data.reverseGeocoderResponse
          if (reverseGeocoderResponse && reverseGeocoderResponse.status === 0) {
            const result = reverseGeocoderResponse.result
            if (result) {
              let fullAddress = ''

              // 优先使用formatted_addresses中的recommend（推荐地址）
              if (result.formatted_addresses && result.formatted_addresses.recommend) {
                fullAddress = result.formatted_addresses.recommend
                console.log('使用recommend地址:', fullAddress)
              } else if (result.formatted_addresses && result.formatted_addresses.complete) {
                // 如果没有推荐地址，使用complete（完整地址）
                fullAddress = result.formatted_addresses.complete
                console.log('使用complete地址:', fullAddress)
              } else if (result.address) {
                // 如果还没有，使用address字段
                fullAddress = result.address
                console.log('使用address字段:', fullAddress)
              } else if (result.address_component) {
                // 最后手动拼接详细地址组件
                const component = result.address_component
                console.log('手动拼接地址，组件:', component)
                const parts = []

                // 按层级拼接：省、市、区、街道、门牌号
                if (component.province && component.province !== component.city) {
                  parts.push(component.province)
                }
                if (component.city) {
                  parts.push(component.city)
                }
                if (component.district) {
                  parts.push(component.district)
                }
                if (component.street) {
                  parts.push(component.street)
                }
                if (component.street_number) {
                  parts.push(component.street_number)
                }

                fullAddress = parts.join('')
                console.log('拼接后的完整地址:', fullAddress)
              }

              // 如果仍然没有获取到地址，使用坐标
              if (!fullAddress || fullAddress.trim() === '') {
                fullAddress = `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`
                console.log('使用坐标作为地址:', fullAddress)
              }

              console.log('最终选择的地址:', fullAddress)

              // 创建信息窗口显示详细信息
              // 确定选中位置的显示内容：优先使用location（地址名称），其次使用"坐标定位"
              const selectedPosition = this.location && this.location.trim() ? this.location : '坐标定位'
              this.createInfoWindow(latLng, selectedPosition, fullAddress, coords)
            } else {
              console.warn('反向地理编码返回无效结果')
              const selectedPosition = this.location && this.location.trim() ? this.location : '坐标定位'
              this.createInfoWindow(latLng, selectedPosition, `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`, coords)
            }
          } else {
            console.warn('反向地理编码失败:', reverseGeocoderResponse.message || '未知错误')
            const selectedPosition = this.location && this.location.trim() ? this.location : '坐标定位'
            this.createInfoWindow(latLng, selectedPosition, `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`, coords)
          }
        } else {
          console.warn('反向地理编码请求失败:', response.data.message || '未知错误')
          const selectedPosition = this.location && this.location.trim() ? this.location : '坐标定位'
          this.createInfoWindow(latLng, selectedPosition, `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`, coords)
        }
      }).catch(error => {
        console.error('反向地理编码请求异常:', error)
        const selectedPosition = this.location && this.location.trim() ? this.location : '坐标定位'
        this.createInfoWindow(latLng, selectedPosition, `位置 ${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`, coords)
      })
    },
    // 创建信息窗口
    createInfoWindow (position, selectedPosition, selectedAddress, selectedCoordinates) {
      // 关闭之前的信息窗口
      if (this.infoWindow) {
        this.infoWindow.close()
        this.infoWindow.destroy()
      }

      // 构建信息窗口内容
      const content = `
        <div style="padding: 10px; min-width: 200px;">
          <div style="margin-bottom: 8px;">
            <span style="color: #909399; font-size: 12px;">选中位置：</span>
            <span style="color: #303133; font-size: 12px;">${selectedPosition}</span>
          </div>
          <div style="margin-bottom: 8px;">
            <span style="color: #909399; font-size: 12px;">选中地址：</span>
            <span style="color: #303133; font-size: 12px;">${selectedAddress}</span>
          </div>
          <div>
            <span style="color: #909399; font-size: 12px;">坐标：</span>
            <span style="color: #606266; font-size: 12px; font-family: monospace;">${selectedCoordinates}</span>
          </div>
        </div>
      `

      // 创建信息窗口
      this.infoWindow = new window.TMap.InfoWindow({
        map: this.map,
        position: position,
        content: content,
        enableCustom: false
      })

      // 显示信息窗口
      this.infoWindow.open()
    },
    // 添加500米圆圈
    addCircle (position) {
      if (this.circle) {
        this.circle.destroy()
      }
      // 创建圆圈（半径由配置决定）
      this.circle = new window.TMap.MultiCircle({
        map: this.map,
        styles: {
          'circle': new window.TMap.CircleStyle({
            'showBorder': true,
            'borderColor': 'rgba(55, 119, 255, 0.8)',
            'borderWidth': 1,
            'fillColor': 'rgba(55, 119, 255, 0.3)'
          })
        },
        geometries: [{
          id: 'circle1',
          styleId: 'circle',
          center: position,
          radius: this.circleRadius // 圆圈半径由配置决定
        }]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-footer {
  text-align: center;
}
</style>
